import { Component, OnInit, signal, effect } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { ProductService } from '../../services/product.service';
import { SearchService } from '../../services/search.service';
import { Product } from '../../models/product.model';

@Component({
  selector: 'app-search-bar',
  imports: [CommonModule, FormsModule],
  templateUrl: './search-bar.component.html',
  styleUrl: './search-bar.component.scss'
})
export class SearchBarComponent implements OnInit {
  protected searchQuery = signal('');
  protected suggestions = signal<Product[]>([]);
  protected showSuggestions = signal(false);
  protected isLoading = signal(false);
  
  private searchSubject = new Subject<string>();

  constructor(
    private productService: ProductService,
    private searchService: SearchService,
    private router: Router
  ) {
    // Inicializa a busca quando produtos são carregados
    this.productService.getProducts().subscribe(products => {
      this.searchService.initializeSearch(products);
    });
  }

  ngOnInit(): void {
    // Debounce na busca (300ms)
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(query => {
      this.performSearch(query);
    });
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const query = target.value;
    this.searchQuery.set(query);
    
    if (query.trim().length >= 2) {
      this.isLoading.set(true);
      this.searchSubject.next(query);
    } else {
      this.suggestions.set([]);
      this.showSuggestions.set(false);
      this.isLoading.set(false);
    }
  }

  performSearch(query: string): void {
    if (query.trim().length < 2) {
      this.isLoading.set(false);
      return;
    }

    const results = this.searchService.searchSuggestions(query, 8);
    this.suggestions.set(results);
    this.showSuggestions.set(true);
    this.isLoading.set(false);
  }

  onSearchSubmit(event: Event): void {
    event.preventDefault();
    const query = this.searchQuery();
    
    if (query.trim().length >= 2) {
      this.hideSuggestions();
      this.router.navigate(['/produtos'], {
        queryParams: { q: query.trim() }
      });
    }
  }

  selectSuggestion(product: Product): void {
    this.hideSuggestions();
    this.searchQuery.set('');
    this.router.navigate(['/produtos', product.id]);
  }

  hideSuggestions(): void {
    // Pequeno delay para permitir click em sugestões
    setTimeout(() => {
      this.showSuggestions.set(false);
    }, 200);
  }

  highlightMatch(text: string): string {
    return this.searchService.highlightMatches(text, this.searchQuery());
  }

  formatPrice(price: number): string {
    return price.toLocaleString('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    });
  }
}

